
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { feedbackService } from '@/services/feedback';
import { useUser } from '@/contexts/UserContext';
import { useNavigate } from 'react-router-dom';

interface StarRatingProps {
  rating: number;
  setRating: (rating: number) => void;
}

const StarRating = ({ rating, setRating }: StarRatingProps) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => setRating(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="text-3xl focus:outline-none transition-transform hover:scale-110 focus:scale-110"
        >
          {star <= (hoverRating || rating) ? (
            <span className="text-yellow-500">★</span>
          ) : (
            <span className="text-muted-foreground">☆</span>
          )}
        </button>
      ))}
    </div>
  );
};

interface RecipeFeedbackFormProps {
  recipeId: string;
  onFeedbackAdded: () => void;
}

const RecipeFeedbackForm = ({ recipeId, onFeedbackAdded }: RecipeFeedbackFormProps) => {
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to leave feedback');
      navigate('/auth/login');
      return;
    }
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await feedbackService.saveFeedback(
        recipeId, 
        comment.trim() || "", 
        rating
      );
      
      // Reset form
      setComment('');
      setRating(0);
      
      // Refresh the feedback list
      onFeedbackAdded();
      
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-3">
        <label htmlFor="rating" className="block text-sm font-medium">
          Rating
        </label>
        <StarRating rating={rating} setRating={setRating} />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="comment" className="block text-sm font-medium">
          Comment (optional)
        </label>
        <Textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your thoughts about this recipe..."
          className="w-full"
          rows={3}
        />
      </div>
      
      <div className="flex justify-end">
        <Button 
          type="submit" 
          disabled={isSubmitting || rating === 0}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </div>
    </form>
  );
};

export default RecipeFeedbackForm;
